import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { Contract } from '../types';

interface ContractDocumentsProps {
  contract: Contract;
  onUpdate?: () => void;
}

interface DocumentFile {
  filename: string;
  original_filename?: string;
  description?: string;
  file_type?: string;
  file_size?: number;
  upload_date?: string;
  uploaded_by?: string;
  document_type?: 'contract' | 'invoice' | 'amendment' | 'other';
  metadata?: {
    pages?: number;
    version?: string;
    tags?: string[];
  };
}

const ContractDocuments: React.FC<ContractDocumentsProps> = ({ contract, onUpdate }) => {
  const [documents, setDocuments] = useState<DocumentFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState<string>('contract');
  const [activeTab, setActiveTab] = useState<'all' | 'contracts' | 'invoices' | 'amendments' | 'other'>('all');

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.axiosInstance.get(
        `/api/v1/contracts/${contract.contract_id}/documents/`
      );
      
      setDocuments(response.data.documents || []);
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      
      // Fallback to PDF endpoint if documents endpoint doesn't exist
      if (error.response?.status === 404) {
        try {
          const pdfResponse = await apiService.axiosInstance.get(
            `/api/v1/contracts/${contract.contract_id}/pdfs/`
          );
          
          // Transform PDF data to document format
          const pdfDocuments = (pdfResponse.data.pdfs || []).map((pdf: any) => ({
            ...pdf,
            file_type: 'pdf',
            document_type: 'contract',
            upload_date: new Date().toLocaleString(), // Use locale-specific time string
            file_size: 0,
          }));
          
          setDocuments(pdfDocuments);
        } catch (pdfError) {
          console.error('Error fetching PDFs:', pdfError);
          setDocuments([]);
        }
      } else {
        setDocuments([]);
      }
    } finally {
      setLoading(false);
    }
  }, [contract.contract_id]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    if (!contract.company_id) {
      alert('Please assign an invoicing company to this contract before uploading documents.');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('description', description);
      formData.append('document_type', documentType);

      // Use the documents endpoint
      const endpoint = `/api/v1/contracts/${contract.contract_id}/documents/upload`;

      await apiService.axiosInstance.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Reset form
      setSelectedFile(null);
      setDescription('');
      setDocumentType('contract');
      
      // Refresh documents list
      fetchDocuments();
      
      // Notify parent component
      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      console.error('Error uploading document:', error);
      alert(error.response?.data?.detail || 'Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (filename: string, fileType: string) => {
    try {
      const endpoint = `/api/v1/contracts/${contract.contract_id}/documents/${filename}`;
        
      const response = await apiService.axiosInstance.get(endpoint, {
        responseType: 'blob',
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document');
    }
  };

  const handleDelete = async (filename: string, fileType: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const endpoint = `/api/v1/contracts/${contract.contract_id}/documents/${filename}`;
        
      await apiService.axiosInstance.delete(endpoint);
      fetchDocuments();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      alert(error.response?.data?.detail || 'Error deleting document');
    }
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return '🖼️';
      default:
        return '📎';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      contract: 'Contract',
      invoice: 'Invoice',
      amendment: 'Amendment',
      other: 'Other'
    };
    return labels[type] || 'Document';
  };

  const getDocumentTypeBadgeClass = (type: string) => {
    const classes: { [key: string]: string } = {
      contract: 'badge-primary',
      invoice: 'badge-success',
      amendment: 'badge-warning',
      other: 'badge-secondary'
    };
    return classes[type] || 'badge-secondary';
  };

  const filteredDocuments = activeTab === 'all' 
    ? documents 
    : documents.filter(doc => doc.document_type === activeTab.slice(0, -1));

  if (!contract.company_id) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h3 style={{ color: '#7f8c8d', marginBottom: '10px' }}>No Invoicing Company Assigned</h3>
          <p style={{ color: '#7f8c8d' }}>
            Please assign an invoicing company to this contract to manage documents.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Contract Documents</h3>
      </div>

      {/* Upload Section */}
      <div style={{ padding: '20px', borderBottom: '1px solid #e0e0e0' }}>
        <h4 style={{ marginBottom: '15px' }}>Upload New Document</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '10px', alignItems: 'flex-end' }}>
          <div>
            <label htmlFor="document-file" style={{ display: 'block', marginBottom: '5px' }}>
              Select File
            </label>
            <input
              id="document-file"
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label htmlFor="document-type" style={{ display: 'block', marginBottom: '5px' }}>
              Document Type
            </label>
            <select
              id="document-type"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              disabled={uploading}
              className="form-control"
            >
              <option value="contract">Contract</option>
              <option value="invoice">Invoice</option>
              <option value="amendment">Amendment</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '5px' }}>
              Description (optional)
            </label>
            <input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Signed contract, Amendment #1"
              disabled={uploading}
              className="form-control"
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
        <p style={{ fontSize: '0.875em', color: '#6c757d', marginTop: '10px' }}>
          Supported formats: PDF, Word (DOC/DOCX), Excel (XLS/XLSX), Images (JPG/PNG)
        </p>
      </div>

      {/* Document Type Tabs */}
      <div className="tabs" style={{ borderBottom: '1px solid #e0e0e0' }}>
        <div className="tab-list" style={{ padding: '0 20px' }}>
          <button
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Documents ({documents.length})
          </button>
          <button
            className={`tab ${activeTab === 'contracts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contracts')}
          >
            Contracts ({documents.filter(d => d.document_type === 'contract').length})
          </button>
          <button
            className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            Invoices ({documents.filter(d => d.document_type === 'invoice').length})
          </button>
          <button
            className={`tab ${activeTab === 'amendments' ? 'active' : ''}`}
            onClick={() => setActiveTab('amendments')}
          >
            Amendments ({documents.filter(d => d.document_type === 'amendment').length})
          </button>
          <button
            className={`tab ${activeTab === 'other' ? 'active' : ''}`}
            onClick={() => setActiveTab('other')}
          >
            Other ({documents.filter(d => d.document_type === 'other').length})
          </button>
        </div>
      </div>

      {/* Documents List */}
      <div style={{ padding: '20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center' }}>Loading documents...</div>
        ) : filteredDocuments.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#7f8c8d' }}>
            {activeTab === 'all' ? 'No documents uploaded yet' : `No ${activeTab} uploaded yet`}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>Type</th>
                  <th>Filename</th>
                  <th>Document Type</th>
                  <th>Description</th>
                  <th>Size</th>
                  <th>Upload Date</th>
                  <th>Uploaded By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((doc) => (
                  <tr key={doc.filename}>
                    <td style={{ fontSize: '1.5em', textAlign: 'center' }}>
                      {getFileIcon(doc.file_type || 'unknown')}
                    </td>
                    <td>
                      <div>
                        <strong>{doc.original_filename || doc.filename}</strong>
                        {doc.metadata?.version && (
                          <span style={{ marginLeft: '10px', fontSize: '0.875em', color: '#6c757d' }}>
                            v{doc.metadata.version}
                          </span>
                        )}
                      </div>
                      {doc.filename !== doc.original_filename && (
                        <div style={{ fontSize: '0.875em', color: '#6c757d' }}>
                          {doc.filename}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${getDocumentTypeBadgeClass(doc.document_type || 'other')}`}>
                        {getDocumentTypeLabel(doc.document_type || 'other')}
                      </span>
                    </td>
                    <td>{doc.description || '-'}</td>
                    <td>{formatFileSize(doc.file_size || 0)}</td>
                    <td>
                      {doc.upload_date 
                        ? new Date(doc.upload_date).toLocaleDateString() 
                        : '-'
                      }
                    </td>
                    <td>{doc.uploaded_by || '-'}</td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleDownload(doc.filename, doc.file_type || 'unknown')}
                        style={{ marginRight: '5px' }}
                      >
                        Download
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(doc.filename, doc.file_type || 'unknown')}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Storage Info */}
      {contract.company && (
        <div style={{ padding: '20px', borderTop: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
          <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9em' }}>
            Documents are stored in: <strong>{contract.company.name}</strong> folder
          </p>
        </div>
      )}
    </div>
  );
};

export default ContractDocuments;